import Header from "@layout/header/header";
import TailwindToggle from "@components/search/TailwindToggle";
import request from "@libs/request/request";
import CustomRangePicker from "../CustomRangePicker";
import useFilterPreview from "./useFilterPreview";
import { IoClose } from "react-icons/io5";
import { useRouter } from "next/router";
import { BsArrowUp } from "react-icons/bs";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Image, Input, Skeleton } from "antd";
import { IoSearchOutline } from "react-icons/io5";
import { VscLoading } from "react-icons/vsc";
import { RxMixerHorizontal } from "react-icons/rx";
import { SlLocationPin } from "react-icons/sl";
import moment from "moment";
import { debounce } from "lodash";
import { motion } from "framer-motion";

const MobileSearchBar = ({
  open,
  setOpen,
  sports,
  isLoading,
  mainTitle,
  filters,
  filterData,
  totalOffers,
  setOpenFilter,
  setOpenMap,
  activeFiltersCount,
}) => {
  const [step, setStep] = useState(1);
  // Sport - step 1
  const [sportValue, setSportValue] = useState();

  // Destinations - step 2
  const [destSearchValue, setDestSearchValue] = useState("");
  const [selectedDest, setSelectedDest] = useState();
  const [destinations, setDestinations] = useState();
  const [defaultDests, setDefaultDests] = useState();
  const [isDestLoading, setIsDestLoading] = useState(false);
  const [hasDestBeenSelected, setHasDestBeenSelected] = useState(false);
  const [hasSearchedDest, setHasSearchedDest] = useState(false);

  // Dates - step 3
  const [isFlexibale, setIsFlexibale] = useState(true);
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);
  const [calendarData, setCalendarData] = useState();
  const [selectedMonth, setSelectedMonth] = useState([]);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const router = useRouter();
  const { t, i18n } = useTranslation("common");
  const isEn = i18n.language === "en";
  const dateFormat = isEn ? "DD/MM/YYYY" : "DD.MM.YYYY";

  // Update filters value if filters were already selected
  const {
    sportPreviewVal,
    destPreviewVal,
    datePreviewVal,
    clearDests,
    clearDates,
  } = useFilterPreview({
    filters,
    filterData,
    isEn,
    dateFormat,
    router,
    sports,
  });

  const changeStep = (stp) => {
    setStep(stp);
  };

  const handleResetData = () => {
    setStep(1);
    setSportValue();
    setDestSearchValue("");
    setSelectedDest();
    setDestinations();
    setDefaultDests();
    setHasDestBeenSelected(false);
    setCalendarData();
    setSelectedMonth([]);
    setStartDate();
    setEndDate();
    setHasSearchedDest(false);
  };

  const handlePostParams = (filters, selectedFilter, type) => {
    const queryParams = new URLSearchParams();

    // sport logic
    if (type === "sport") {
      if (selectedFilter?.alias) {
        const existingSports = filters.sport ? String(filters.sport) : "";
        const sportsArray = existingSports.split(",").filter(Boolean);

        if (!sportsArray.includes(selectedFilter.alias)) {
          sportsArray.push(selectedFilter.alias);
        }

        filters.sport = sportsArray.join(",");
        queryParams.set("sport", selectedFilter.alias);
      } else {
        ["sport", "sub_sport"].forEach((key) => {
          const value = filters[key];
          if (value?.length === 0 || value === "") return;

          if (Array.isArray(value) && value.length > 0) {
            queryParams.set(key, value.join(","));
          } else {
            queryParams.set(key, value);
          }
        });
      }
    } else {
      // dest logic
      const allDestinations = new Set([
        ...(filters.continent || []),
        ...(filters.country || []),
        ...(filters.state || []),
      ]);
      if (selectedFilter?.alias) {
        if (selectedFilter.alias !== "0") {
          queryParams.set("destination_alias", selectedFilter.alias);
        }
      } else {
        const destinationAliases = Array.from(allDestinations).filter(Boolean);
        if (destinationAliases.length > 0) {
          queryParams.set("destination_alias", destinationAliases.join(","));
        }
      }
    }

    return queryParams.toString();
  };

  const handleSportSelection = async (selectedSport) => {
    try {
      setSportValue(selectedSport);
      setIsDestLoading(true);
      changeStep(2);
      clearDests();
      clearDates();
      setSelectedMonth([]);
      setStartDate(null);
      setEndDate(null);

      const params = handlePostParams({ ...filters }, selectedSport, "sport");

      const res = await request.get(
        `search/v2/destination${params ? `?${params}` : ""}`
      );
      setDestinations(res?.data);
      setDefaultDests(res?.data);
      setHasSearchedDest(false);
    } catch (err) {
      console.log(err.response?.data || err.message);
    } finally {
      setIsDestLoading(false);
    }
  };

  const getDateData = async (selectedDest) => {
    try {
      setIsCalendarLoading(true);

      const sportParams = handlePostParams({ ...filters }, sportValue, "sport");
      const destParams = handlePostParams({ ...filters }, selectedDest, "dest");

      const res = await request.get(
        `/search/v2/travel-date${sportParams ? `?${sportParams}` : ""}${
          destParams ? `${sportParams ? "&" : "?"}${destParams}` : ""
        }`
      );

      setCalendarData(res?.data);
    } catch (err) {
      console.log(err.response?.data || err.message);
    } finally {
      setIsCalendarLoading(false);
    }
  };

  // Get latest destinations - Request on load
  const getLatestDestinations = async () => {
    try {
      setIsDestLoading(true);
      const res = await request.get("search/v2/last-search");
      setDestinations(res?.data);
      setDefaultDests(res?.data);
      setHasSearchedDest(false);
    } catch (err) {
      console.log(err.response?.data || err.message);
    } finally {
      setIsDestLoading(false);
    }
  };

  // Memoize Debounced destinations search
  const debouncedSearch = useMemo(
    () =>
      debounce(async (value, params) => {
        const trimmedValue = value.trim();

        const queryParams = `?search=${encodeURIComponent(trimmedValue)}${
          params ? `&${params}` : ""
        }`;

        try {
          const result = await request.get(
            `search/v2/destination${queryParams}`
          );
          setDestinations(result?.data || {});
          setHasSearchedDest(
            !result?.data?.continents ||
              !result?.data?.countries ||
              !result?.data?.states
          );
        } catch (error) {
          console.log("Error:", error.response || error.message);
        } finally {
          setIsDestLoading(false);
        }
      }, 500), // 500ms delay
    [sportValue]
  );

  // Get destinations after type-in search
  const handleDestSearch = async (value) => {
    setHasDestBeenSelected(false);
    setDestSearchValue(value);
    setIsDestLoading(true);
    setHasSearchedDest(value && value !== "");
    const params = handlePostParams({ ...filters }, sportValue, "sport");

    // Call API with 500ms delay
    debouncedSearch(value, params);
  };

  const handleDestSelection = async (selectedDest, type) => {
    if (!selectedDest) return;

    const isAll = selectedDest === "all";
    const temp = {
      alias: isAll ? "0" : selectedDest?.alias,
      id: isAll ? "0" : selectedDest?.id,
      title: isAll
        ? t("frontpage.all_des")
        : selectedDest?.alias === "galapagos-inseln"
        ? isEn
          ? "Galapagos Islands"
          : "Galapagos Inseln"
        : selectedDest?.title?.en || selectedDest?.title?.de
        ? isEn
          ? selectedDest?.title?.en ?? selectedDest?.title?.de
          : selectedDest?.title?.de ?? selectedDest?.title?.en
        : selectedDest?.title,
      type: type,
      displayTitle: isAll
        ? "all"
        : selectedDest?.alias === "galapagos-inseln"
        ? {
            en: "Galapagos Islands",
            de: "Galapagos Inseln",
          }
        : selectedDest?.title,
    };

    setDestSearchValue(temp.title);
    setSelectedDest(temp);
    getDateData(temp);
    setIsCalendarLoading(true);
    setHasDestBeenSelected(true);
    setHasSearchedDest(false);
    changeStep(3);
  };

  // Handle search btn click
  const applyFilters = () => {
    // build query params - URL encoding
    const buildQueryParam = (key, value) =>
      `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;

    // build date range query params
    const buildDateRangeParam = (start, end) => {
      const formattedStart = moment(start).format("YYYY-MM-DD");
      const formattedEnd = moment(end).format("YYYY-MM-DD");
      return `from=${formattedStart}&to=${formattedEnd}`;
    };

    // handle month array and convert it to query string
    const buildMonthParam = (months) =>
      months?.map((month) => buildQueryParam("month", month)).join("&");

    const getDestType = (type) =>
      // type === "continent"
      //   ? "continents"
      //   : type === "country"
      //   ? "countries"
      //   : type === "state"
      //   ? "states"
      //   : "countries";
      type === "continent"
        ? "continent"
        : type === "country"
        ? "country"
        : type === "state"
        ? "state"
        : "country";

    const queryParams = [];

    // Sport Filter
    if (sportValue?.alias) {
      if (sportValue?.alias === "0") {
        queryParams.push(buildQueryParam("all_sports", true));
      } else {
        queryParams.push(buildQueryParam("sport", sportValue?.alias));
      }
    } else if (sportPreviewVal?.length > 0 && !sportValue?.alias) {
      // include already selected sports
      sportPreviewVal?.map((sport) =>
        sport?.alias === "all"
          ? queryParams.push(buildQueryParam("sport", true))
          : queryParams.push(buildQueryParam(sport?.type, sport?.alias))
      );
    }

    // destination Filter
    if (selectedDest?.alias) {
      if (selectedDest?.alias === "0") {
        queryParams.push(buildQueryParam("all_destinations", true));
      } else {
        const countryKey = getDestType(selectedDest?.type);

        // Handle URL encoding for countries with "&" in their names
        const encodedCountry = selectedDest?.alias?.includes("&")
          ? selectedDest?.alias.replace("&", "%26")
          : selectedDest?.alias;

        queryParams.push(buildQueryParam(countryKey, encodedCountry));
        localStorage.setItem("destinationFilter", JSON.stringify(selectedDest));
      }
    } else if (destPreviewVal?.length > 0 && !selectedDest?.alias) {
      destPreviewVal?.map((dest) => {
        const countryKey = getDestType(dest?.type);

        dest?.alias === "all"
          ? queryParams.push(buildQueryParam("all_destinations", true))
          : dest?.alias !== selectedDest?.alias &&
            queryParams.push(buildQueryParam(countryKey, dest?.alias));
      });
    }

    // Date Range Filter - speecific dates
    if (startDate && endDate) {
      queryParams.push(buildDateRangeParam(startDate, endDate));
    }

    // Month Filter - flexibile dates
    if (selectedMonth?.length > 0 && Array.isArray(selectedMonth)) {
      queryParams.push(buildMonthParam(selectedMonth));
    }

    // Filter out any empty params before joining with "&"
    const finalQueryString = queryParams.filter(Boolean).join("&");

    // Update URL
    if (queryParams?.length > 0 && finalQueryString) {
      router.push(`${router.pathname}?page=1&${finalQueryString}`, undefined, {
        shallow: true,
      });
    } else {
      router.push(router.pathname, undefined, {
        shallow: true,
      });
    }

    // close drawer
    setOpen(false);
    handleResetData();
  };

  const handleSubtitle = () => {
    if (
      sportPreviewVal?.length > 0 &&
      destPreviewVal?.length > 0 &&
      datePreviewVal?.length > 0
    ) {
      // Case a: All selected
      return datePreviewVal?.map(
        (date, i) => `${i >= 1 ? " - " : ""} ${date?.title}`
      );
    } else if (
      sportPreviewVal?.length > 0 &&
      destPreviewVal?.length > 0 &&
      datePreviewVal?.length === 0
    ) {
      // Case b: Add time period
      return isEn ? `Add time period` : `Zeitraum hinzufügen`;
    } else if (
      sportPreviewVal?.length > 0 &&
      destPreviewVal?.length === 0 &&
      datePreviewVal?.length > 0
    ) {
      // Case c: Time period displayed with sport
      return datePreviewVal?.map(
        (date, i) => `${i >= 1 ? " - " : ""} ${date?.title}`
      );
    } else if (
      sportPreviewVal?.length === 0 &&
      destPreviewVal?.length > 0 &&
      datePreviewVal?.length > 0
    ) {
      // Case c: Time period displayed with destination
      return datePreviewVal?.map(
        (date, i) => `${i >= 1 ? " - " : ""} ${date?.title}`
      );
    } else if (
      (sportPreviewVal?.length > 0 || destPreviewVal?.length > 0) &&
      datePreviewVal?.length === 0
    ) {
      // Case d: Add sport/destination and time period
      return isEn
        ? `Add ${
            sportPreviewVal.length === 0 ? "type of sport" : "destination"
          } and time period`
        : `${
            sportPreviewVal.length === 0 ? "Sportart" : "Reiseziel"
          } und Zeitraum hinzufügen`;
    } else {
      // Case e: Default selection prompt
      return isEn
        ? `Select type of sport, destination and time period`
        : `Sportart, Reiseziel und Zeitraum auswählen`;
    }
  };

  return (
    <div>
      {/* filters box */}
      <div className="w-full relative pt-7 container px-4 mx-auto">
        <div
          className="px-4 border-[2.5px] border-green rounded-[15px] bg-white cursor-pointer min-h-[92px] flex flex-col items-center justify-center"
          onClick={() => setOpen(true)}
        >
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 1 }} className="!-mt-4" />
          ) : (
            <div className="py-3 w-full">
              <h1 className="text-[20px] font-bold">
                {isEn
                  ? mainTitle?.en ?? "Sport & active journeys"
                  : mainTitle?.de ?? "Sport- & Aktivreisen"}
              </h1>
              <h2 className="text-base font-normal mt-1 line-clamp-2">
                {handleSubtitle()}
              </h2>
            </div>
          )}
        </div>

        <div className="w-full flex items-center justify-evenly gap-2 py-5">
          <p className="text-base font-medium">
            {totalOffers <= 1
              ? `${totalOffers} ${t("search.result")}`
              : `${totalOffers} ${t("search.results")}`}
          </p>

          <div
            className="px-5 py-2 bg-[#e3f9fc] rounded-full flex items-center gap-2 cursor-pointer relative"
            onClick={() => setOpenMap(true)}
          >
            <div>
              <Image
                src="/static/icons/map.svg"
                alt="map"
                width={16}
                height={16}
                preview={false}
                loading="lazy"
                className="!mt-[2px]"
              />
            </div>
            <p className="text-sm font-medium whitespace-nowrap">
              {t("offer.mapView")}
            </p>
          </div>
          <div
            className={`px-5 py-2 bg-[#e3f9fc] rounded-full flex items-center gap-2 cursor-pointer relative ${
              activeFiltersCount > 0 ? "!border !border-green" : ""
            }`}
            onClick={() => setOpenFilter(true)}
          >
            {activeFiltersCount > 0 && (
              <span className="absolute px-[7px] py-[0.5px] rounded-full bg-green text-white text-[12px] -top-[7px] right-0 font-medium">
                {activeFiltersCount}
              </span>
            )}
            <RxMixerHorizontal className="text-[22px]" />
            <p className="text-sm font-medium whitespace-nowrap">
              {t("offer.open_drawer_filter")}
            </p>
          </div>
        </div>
      </div>

      {/* Mobile search drawer */}
      {open && (
        <Drawer
          open={open}
          width="100%"
          height="100%"
          closable={false}
          headerStyle={{ display: "none" }}
          className="[&_.ant-drawer-body]:!p-0"
          keyboard
          destroyOnClose
          footer={
            <div className="w-full px-2">
              <Button
                className="w-full rounded-3xl h-[44px] !bg-orange !text-white !font-medium !text-base"
                onClick={applyFilters}
                aria-label={t("frontpage.search_mobile")}
              >
                {t("frontpage.search_mobile")}
              </Button>
            </div>
          }
        >
          {/* Top moverii header */}
          <Header router={router} />

          {/* Tabs - steps */}
          <div className="border-b pt-5 sticky top-0 bg-white">
            <div className="w-full flex items-center justify-between gap-2 container mx-auto px-4">
              <div className="flex items-center gap-3">
                <p
                  className={`text-base cursor-pointer pb-3 transition-all duration-100 ${
                    step === 1
                      ? "border-b-2 border-orange font-bold text-black"
                      : "font-normal text-gray-50"
                  }`}
                  onClick={() => changeStep(1)}
                >
                  {t("frontpage.sport_select")}
                </p>
                <p
                  className={`text-base cursor-pointer pb-3 transition-all duration-100 ${
                    step === 2
                      ? "border-b-2 border-orange font-bold text-black"
                      : "font-normal text-gray-50"
                  }`}
                  onClick={() => {
                    changeStep(2);
                    if (
                      !sportValue &&
                      !destinations &&
                      !defaultDests &&
                      sportPreviewVal?.length === 0
                    ) {
                      getLatestDestinations();
                    } else if (sportPreviewVal?.length > 0) {
                      handleSportSelection();
                    }
                  }}
                >
                  {t("frontpage.destination_select")}
                </p>
                <p
                  className={`text-base cursor-pointer pb-3 transition-all duration-100 ${
                    step === 3
                      ? "border-b-2 border-orange font-bold text-black"
                      : "font-normal text-gray-50"
                  }`}
                  onClick={() => {
                    changeStep(3);
                    getDateData();
                  }}
                >
                  {t("frontpage.travel_date")}
                </p>
              </div>

              <div>
                <IoClose
                  className="text-2xl text-gray-40 hover:text-black transition-all duration-200 cursor-pointer -mt-3"
                  onClick={() => {
                    setOpen(false);
                    handleResetData();
                  }}
                />
              </div>
            </div>
          </div>

          {/* content */}
          <div className="container mx-auto px-4 pt-4">
            {/* Step 1 - sports */}
            {step === 1 && (
              <motion.div
                className="w-full"
                initial={{ opacity: 0.1, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.1,
                  type: "spring",
                  stiffness: 300,
                  damping: 50,
                }}
              >
                <ul className="w-full flex flex-col gap-3">
                  {sports?.map((sport, i) => {
                    const isSelected = sportValue?.alias === sport?.alias;
                    return (
                      <li
                        key={sport?.alias + i}
                        className={`w-full py-3 border-2 px-4 rounded-xl flex items-center justify-between cursor-pointer ${
                          isSelected ? "border-orange" : "border-[#12A3BA]"
                        }`}
                        onClick={() => handleSportSelection(sport)}
                      >
                        <p
                          className={`text-base ${
                            isSelected ? "font-bold" : "font-normal"
                          }`}
                        >
                          {sport.title}
                        </p>
                        {isSelected ? (
                          <Image
                            src="/icon/search/orange_checked.png"
                            alt="checked mark"
                            preview={false}
                            width="20px"
                            height="20px"
                          />
                        ) : (
                          <BsArrowUp className="text-xl rotate-90" />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            )}

            {/* step 2 - Destinations */}
            {step === 2 && (
              <motion.div
                className="w-full"
                initial={{ opacity: 0.1, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.1,
                  type: "spring",
                  stiffness: 300,
                  damping: 50,
                }}
              >
                <Input
                  value={destSearchValue}
                  placeholder={t("frontpage.mobile_search_destination")}
                  onChange={(e) => handleDestSearch(e.target.value)}
                  showAction={["focus", "click"]}
                  className="my-1 py-3 px-4 rounded-xl !border-2 !border-[#12A3BA] flex flex-row search-border"
                  suffix={
                    <span>
                      {isDestLoading ? (
                        <VscLoading className="text-2xl text-[#959499] animate-spin" />
                      ) : destSearchValue ? (
                        <IoClose
                          className="text-2xl text-black cursor-pointer"
                          onClick={() => {
                            setDestSearchValue("");
                            setDestinations(defaultDests);
                            setHasDestBeenSelected(false);
                            localStorage.removeItem("destinationFilter");
                          }}
                        />
                      ) : (
                        <IoSearchOutline className="text-2xl text-[#959499]" />
                      )}
                    </span>
                  }
                />

                {!hasDestBeenSelected && (
                  <div className="w-full pt-4">
                    {isDestLoading ? (
                      _.range(0, 15).map((item) => (
                        <Skeleton.Button
                          key={"dest-placeholder-" + item}
                          active
                          block
                          className="!h-[30px] !w-full !mb-3"
                        />
                      ))
                    ) : destinations?.continents?.length == 0 &&
                      destinations?.countries?.length == 0 &&
                      destinations?.states?.length == 0 ? (
                      // No destination available
                      <motion.div
                        className="w-full h-full flex flex-col items-center justify-center mt-2"
                        initial={{ opacity: 0.1, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          duration: 0.1,
                          type: "spring",
                          stiffness: 300,
                          damping: 50,
                        }}
                      >
                        <div>
                          <Image
                            src="/icon/search/no-data.png"
                            alt="no data!"
                            preview={false}
                            width={"200px"}
                            height={"200px"}
                            loading="lazy"
                          />
                        </div>
                        <p className="max-w-[90%] text-center text-wrap !text-black font-medium">
                          {t("search.searchbar_no_data_text_dest")}
                        </p>
                      </motion.div>
                    ) : (
                      <ul className="w-full flex flex-col gap-2">
                        {/* All destinations */}
                        <li
                          className="text-base cursor-pointer flex items-center gap-x-2 pl-2"
                          onClick={() => handleDestSelection("all", "country")}
                        >
                          <SlLocationPin className="text-lg" />
                          <p className="font-medium">
                            {t("frontpage.all_des")}
                          </p>
                        </li>

                        {hasSearchedDest ? (
                          <div className="w-full flex flex-col gap-5 mt-3">
                            {destinations?.length > 0 ? (
                              destinations?.map((des, i) => (
                                <li
                                  key={des?.alias + i}
                                  className="text-base font-medium cursor-pointer flex items-center gap-x-2 pl-2"
                                  onClick={() =>
                                    handleDestSelection(des, des?.type)
                                  }
                                >
                                  <SlLocationPin className="text-lg" />
                                  <p>
                                    {des?.title?.en || des?.title?.de
                                      ? isEn
                                        ? des?.title?.en ?? des?.title?.de
                                        : des?.title?.de ?? des?.title?.en
                                      : des?.title}
                                  </p>
                                </li>
                              ))
                            ) : (
                              <motion.div
                                className="w-full h-full flex flex-col items-center justify-center mt-2"
                                initial={{ opacity: 0.1, scale: 0.5 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{
                                  duration: 0.1,
                                  type: "spring",
                                  stiffness: 300,
                                  damping: 50,
                                }}
                              >
                                <div>
                                  <Image
                                    src="/icon/search/no-data.png"
                                    alt="no data!"
                                    preview={false}
                                    width={"200px"}
                                    height={"200px"}
                                    loading="lazy"
                                  />
                                </div>
                                <p className="max-w-[90%] text-center text-wrap !text-black font-medium">
                                  {t("search.searchbar_no_data_text_dest")}
                                </p>
                              </motion.div>
                            )}
                          </div>
                        ) : (
                          <div className="w-full flex flex-col gap-2">
                            {/* continents */}
                            {destinations?.continents?.length > 0 && (
                              <div className="flex flex-col gap-4">
                                <li className="text-base font-bold text-[#959499] mt-4">
                                  <p>{t("searchbar_category_continents")}</p>
                                </li>
                                {destinations?.continents?.map((des, i) => (
                                  <li
                                    key={des?.alias + i}
                                    className="text-base font-medium cursor-pointer flex items-center gap-x-2 pl-2"
                                    onClick={() =>
                                      handleDestSelection(des, "continent")
                                    }
                                  >
                                    <SlLocationPin className="text-lg" />
                                    <p>
                                      {des?.title?.en || des?.title?.de
                                        ? isEn
                                          ? des?.title?.en ?? des?.title?.de
                                          : des?.title?.de ?? des?.title?.en
                                        : des?.title}
                                    </p>
                                  </li>
                                ))}
                              </div>
                            )}

                            {/* countries */}
                            {destinations?.countries?.length > 0 && (
                              <div className="flex flex-col gap-4">
                                <li className="text-base font-bold text-[#959499] mt-4">
                                  <p>{t("searchbar_category_countries")}</p>
                                </li>
                                {destinations?.countries?.map((des, i) => (
                                  <li
                                    key={des?.alias + i}
                                    className="text-base font-medium cursor-pointer flex items-center gap-x-2 pl-2"
                                    onClick={() =>
                                      handleDestSelection(des, "country")
                                    }
                                  >
                                    <SlLocationPin className="text-lg" />
                                    <p>
                                      {des?.title?.en || des?.title?.de
                                        ? isEn
                                          ? des?.title?.en ?? des?.title?.de
                                          : des?.title?.de ?? des?.title?.en
                                        : des?.title}
                                    </p>
                                  </li>
                                ))}
                              </div>
                            )}

                            {/* states */}
                            {destinations?.states?.length > 0 && (
                              <div className="flex flex-col gap-4">
                                <li className="text-base font-bold text-[#959499] mt-4">
                                  <p>{t("searchbar_category_states")}</p>
                                </li>
                                {destinations?.states?.map((des, i) => {
                                  const title =
                                    typeof des?.title === "object" &&
                                    !Array.isArray(des?.title)
                                      ? isEn
                                        ? des?.title?.en ||
                                          des?.title?.de ||
                                          des?.title
                                        : des?.title?.de ||
                                          des?.title?.en ||
                                          des?.title
                                      : des?.title;

                                  return (
                                    <li
                                      key={des?.alias + i}
                                      className="text-base font-medium cursor-pointer flex items-center gap-x-2 pl-2"
                                      onClick={() =>
                                        handleDestSelection(des, "state")
                                      }
                                    >
                                      <SlLocationPin className="text-lg" />
                                      <p>{title}</p>
                                    </li>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </motion.div>
            )}

            {/* step 3 - Dates */}
            {step === 3 && (
              <motion.div
                className="w-full"
                initial={{ opacity: 0.1, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.1,
                  type: "spring",
                  stiffness: 300,
                  damping: 50,
                }}
              >
                <div className="flex w-full items-center justify-center mt-2 mb-4">
                  <TailwindToggle
                    setValue={setIsFlexibale}
                    value={isFlexibale}
                    isMobile={true}
                  />
                </div>

                {isCalendarLoading ? (
                  //  Loading effect
                  <div className="w-full sm:max-w-[350px] grid grid-cols-2 gap-3 mx-auto">
                    {_.range(0, 12).map((item) => (
                      <Skeleton.Button
                        key={("month-loading-placeholder-", item)}
                        active
                        className="!w-full !h-[80px] !rounded-2xl !shadow-md"
                      />
                    ))}
                  </div>
                ) : (
                  <div>
                    {isFlexibale ? (
                      !calendarData?.flexible?.full_access &&
                      calendarData?.flexible?.valid_month.length == 0 ? (
                        // No date available
                        <motion.div
                          className="w-full h-full flex flex-col items-center justify-center mt-2"
                          initial={{ opacity: 0.1, scale: 0.5 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{
                            duration: 0.1,
                            type: "spring",
                            stiffness: 300,
                            damping: 50,
                          }}
                        >
                          <div>
                            <Image
                              src="/icon/search/no-data.png"
                              alt="no data!"
                              preview={false}
                              width={"200px"}
                              height={"200px"}
                              loading="lazy"
                            />
                          </div>
                          <p className="max-w-[90%] text-center text-wrap !text-black font-medium">
                            {t("search.searchbar_no_data_text_dates")}
                          </p>
                        </motion.div>
                      ) : (
                        // Flexible - Month selection
                        <ul className="w-full sm:max-w-[350px] grid grid-cols-2 mx-auto gap-2">
                          {_.range(1, 13).map((monthNum) => {
                            const month = moment()
                              .add(monthNum, "M")
                              .format("YYYY-MM");

                            // if full_access is false, only allow selecting valid_month
                            const isBlocked =
                              !calendarData?.flexible?.full_access &&
                              calendarData?.flexible?.valid_month?.length > 0 &&
                              !calendarData?.flexible?.valid_month?.some(
                                (mo) => moment(mo).format("YYYY-MM") === month
                              );

                            return (
                              <li
                                key={"month-number-" + monthNum}
                                title={moment()
                                  .add(monthNum, "M")
                                  .format(
                                    i18n.language === "en"
                                      ? "YYYY/MM"
                                      : "YYYY.MM"
                                  )}
                                className={`border border-gray-400 rounded-2xl w-full py-3 shadow-sm flex flex-col items-center justify-center transition-all duration-100 px-2 text-sm ${
                                  isBlocked
                                    ? // Block month if not available
                                      "bg-gray-70 !cursor-not-allowed"
                                    : "cursor-pointer bg-white"
                                } ${
                                  selectedMonth?.includes(month)
                                    ? "border-[3px] !border-[#FF9F1C] shadow-md"
                                    : ""
                                }`}
                                onClick={() => {
                                  if (!isBlocked) {
                                    // Check if the month was already selected
                                    if (selectedMonth?.includes(month)) {
                                      // Filter out the selected item
                                      const filteredItems =
                                        selectedMonth?.filter(
                                          (m) => m !== month
                                        );
                                      setSelectedMonth(filteredItems);
                                    } else {
                                      // Add new selected month
                                      setSelectedMonth([
                                        ...selectedMonth,
                                        month,
                                      ]);
                                    }

                                    // Clear specific date value
                                    setStartDate(null);
                                    setEndDate(null);
                                  }
                                }}
                              >
                                <p
                                  className={`${
                                    selectedMonth?.includes(month)
                                      ? "font-bold"
                                      : "font-medium"
                                  } text-base`}
                                >
                                  {moment().add(monthNum, "M").format("MMMM")}
                                </p>
                                <p
                                  className={`${
                                    selectedMonth?.includes(month)
                                      ? "font-medium"
                                      : "font-normal"
                                  } text-base`}
                                >
                                  {moment().add(monthNum, "M").format("YYYY")}
                                </p>
                              </li>
                            );
                          })}
                        </ul>
                      )
                    ) : (
                      <div>
                        {calendarData?.specific?.full_access_calender ==
                          false &&
                        calendarData?.specific?.validDates?.length == 0 ? (
                          // No date available
                          <motion.div
                            className="w-full h-full flex flex-col items-center justify-center mt-2"
                            initial={{ opacity: 0.1, scale: 0.5 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{
                              duration: 0.1,
                              type: "spring",
                              stiffness: 300,
                              damping: 50,
                            }}
                          >
                            <div>
                              <Image
                                src="/icon/search/no-data.png"
                                alt="no data!"
                                preview={false}
                                width={"200px"}
                                height={"200px"}
                                loading="lazy"
                              />
                            </div>
                            <p className="max-w-[90%] text-center text-wrap !text-black font-medium">
                              {t("search.searchbar_no_data_text_dates")}
                            </p>
                          </motion.div>
                        ) : (
                          <CustomRangePicker
                            setStartDate={setStartDate}
                            setEndDate={setEndDate}
                            startDate={startDate}
                            endDate={endDate}
                            data={calendarData?.specific}
                            setSelectedMonth={setSelectedMonth}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </Drawer>
      )}
    </div>
  );
};

export default MobileSearchBar;
